import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ChevronDown, ChevronRight, ScrollText, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import type { PaginatedResponse, PendingCostChange } from '@/lib/api/types';
import { Page, PageBody, PageHeader, Stat, StatRow, Toolbar } from '@/ui/Panel';
import { BulkBar, DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup, Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Textarea } from '@/ui/Field';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel, date, money, num, qty } from '@/lib/format';
import { cn } from '@/lib/cn';
import { costChangeKeys, n, poKeys } from '@/features/buying/api';
import { CostVariance } from '@/features/buying/parts';
import { PurchasingTabs } from '@/features/buying/PurchasingTabs';
import type { BulkReviewResult } from '@/features/buying/types';
import {
  CheckBadges,
  CostDetails,
  MarginCell,
} from '@/features/buying/CostApprovalParts';
import {
  ageText,
  apiSort,
  bulkResultText,
  defaultCostSort,
  impactText,
  referenceLine,
  STALE_REVIEW_CODES,
  totalWeeklyImpact,
  type CostSortKey,
  type SortDir,
} from '@/features/buying/cost-approval';
import { refusalCode } from '@/features/buying/CountConfirmation';

const PAGE_SIZE = 50;
/** `POST /vendors/cost-changes/bulk-review` caps `ids` at 100. */
const BULK_LIMIT = 100;

type Status = 'pending' | 'approved' | 'rejected';

const STATUSES: Array<{ value: Status; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

type CostApprovalSearch = {
  status?: Status;
  page?: number;
  /**
   * Absent = the screen's default for the tab (biggest weekly impact first on Pending). Not
   * `sort` / `order`: the parent Vendors route validates those names for every child and would
   * hand this screen its own defaults (`order=asc`).
   */
  sortBy?: CostSortKey;
  sortDir?: SortDir;
};

export const Route = createFileRoute('/_app/vendors/cost-approvals')({
  staticData: { crumb: 'Costs' },
  validateSearch: (s: Record<string, unknown>): CostApprovalSearch => ({
    status: STATUSES.some((option) => option.value === s.status)
      ? (s.status as Status)
      : 'pending',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
    sortBy: s.sortBy === 'impact' || s.sortBy === 'age' ? s.sortBy : undefined,
    sortDir: s.sortDir === 'asc' || s.sortDir === 'desc' ? s.sortDir : undefined,
  }),
  component: CostApprovals,
});

/** A sortable header is a button, which does not inherit the table header's uppercase. */
const SORT_HEADER = 'uppercase tracking-wider font-medium';

type PendingAction = {
  action: 'approve' | 'reject';
  ids: number[];
  rows: PendingCostChange[];
};

function CostApprovals() {
  const raw = Route.useSearch();
  const search = { status: raw.status ?? ('pending' as Status), page: raw.page ?? 1 };
  const fallbackSort = defaultCostSort(search.status);
  const sortBy: CostSortKey = raw.sortBy ?? fallbackSort.by;
  const sortDir: SortDir =
    raw.sortDir ?? (raw.sortBy === undefined ? fallbackSort.dir : 'desc');
  const sortParams = apiSort(sortBy, sortDir);
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  /** Rows whose details (last costs, warnings) are open. */
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggleOpen = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useShortcutHint([
    { keys: 'Enter', label: 'details' },
    { keys: 'x', label: 'select' },
    { keys: '⌘A', label: 'select page' },
    { keys: 'Esc', label: 'clear' },
  ]);

  const list = useQuery({
    queryKey: costChangeKeys.list({
      status: search.status,
      page: search.page,
      ...sortParams,
    }),
    queryFn: () =>
      api.get<PaginatedResponse<PendingCostChange>>('/vendors/cost-changes', {
        params: {
          status: search.status,
          page: search.page,
          pageSize: PAGE_SIZE,
          ...sortParams,
        },
      }),
  });

  const count = useQuery({
    queryKey: costChangeKeys.count,
    queryFn: () =>
      api.get<{ count: number; retiredByNewerReceipts30d?: number }>(
        '/vendors/cost-changes/count'
      ),
  });

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);

  const settle = () => {
    setPending(null);
    setReviewNotes('');
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: costChangeKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: poKeys.all });
  };

  const single = useMutation({
    // TASK-0252: the cost this screen showed travels with the review; a row replaced by a newer cost
    // (SUPERSEDED), one asking for another amount (COST_CHANGED) or one older than a cost already
    // applied (NEWER_COST_EXISTS) is refused with a sentence, and the list reloads.
    mutationFn: ({
      row,
      action,
    }: {
      row: PendingCostChange;
      action: 'approve' | 'reject';
    }) =>
      api.post(`/vendors/cost-changes/${row.id}/review`, {
        action,
        expectedNewCost: row.newCost,
        ...(reviewNotes.trim() ? { notes: reviewNotes.trim() } : {}),
      }),
    onSuccess: (_data, variables) => {
      settle();
      toast.success(
        variables.action === 'approve'
          ? 'Cost change approved — product cost and average cost updated.'
          : 'Cost change rejected — the product cost is unchanged.'
      );
    },
    onError: (error) => {
      toast.error(errorMessage(error));
      if (STALE_REVIEW_CODES.has(refusalCode(error) ?? '')) settle();
    },
  });

  const bulk = useMutation({
    mutationFn: ({
      rows,
      action,
    }: {
      rows: PendingCostChange[];
      action: 'approve' | 'reject';
    }) =>
      api.post<BulkReviewResult>('/vendors/cost-changes/bulk-review', {
        items: rows.map((row) => ({ id: row.id, expectedNewCost: row.newCost })),
        action,
        ...(reviewNotes.trim() ? { notes: reviewNotes.trim() } : {}),
      }),
    onSuccess: (result, variables) => {
      settle();
      const verb = variables.action === 'approve' ? 'approved' : 'rejected';
      const text = bulkResultText(result, verb);
      if ((result.skipped?.length ?? 0) > 0) toast.warning(text);
      else toast.success(text);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const setSearch = (next: CostApprovalSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }) });

  const selectedRows = useMemo(
    () => rows.filter((row) => selected.has(row.id)),
    [rows, selected]
  );

  const totals = useMemo(() => {
    const impact = totalWeeklyImpact(rows);
    const worst = rows.reduce((max, row) => {
      const pct = Math.abs(n(row.pctChange));
      return pct > max ? pct : max;
    }, 0);
    return { impact, worst };
  }, [rows]);

  const openAction = (action: 'approve' | 'reject', target: PendingCostChange[]) => {
    setReviewNotes('');
    setPending({ action, ids: target.map((row) => row.id), rows: target });
  };

  const columns: Column<PendingCostChange>[] = [
    {
      id: 'product',
      header: 'Product',
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-1.5">
          {open.has(row.id) ? (
            <ChevronDown className="text-ink-faint size-3.5 shrink-0" />
          ) : (
            <ChevronRight className="text-ink-faint size-3.5 shrink-0" />
          )}
          <div className="min-w-0 leading-tight">
            <div className="text-ink truncate">{row.productName}</div>
            <div className="text-2xs text-ink-faint truncate">
              {row.vendorName ?? 'Unknown vendor'}
              {row.invoiceNumber ? ` · invoice ${row.invoiceNumber}` : ''}
              {row.raisedByName ? ` · raised by ${row.raisedByName}` : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'cost',
      header: 'Cost now → new',
      width: '8.5rem',
      mono: true,
      cell: (row) => (
        <div className="leading-tight">
          <div>
            <span className="text-ink-muted">{money(row.oldCost)}</span>
            <span className="text-ink-faint"> → </span>
            <span className="text-ink">{money(row.newCost)}</span>
          </div>
          <CostVariance from={row.oldCost} to={row.newCost} />
        </div>
      ),
    },
    {
      // TASK-0252: what the order's vendor was last paid, and the order's estimate (never a cost).
      id: 'lastPaid',
      header: 'Last paid',
      width: '6.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'xl',
      cell: (row) => (
        <div className="leading-tight" title={referenceLine(row) ?? undefined}>
          <div>{row.vendorLastPaid ? money(row.vendorLastPaid.unitCostCents) : '—'}</div>
          {row.orderEstimateCents != null ? (
            <div className="text-2xs text-ink-faint">
              est. {money(row.orderEstimateCents)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'margin',
      header: 'Margin',
      width: '8rem',
      mono: true,
      cell: (row) => <MarginCell row={row} />,
    },
    {
      id: 'impact',
      header: 'Weekly impact',
      width: '7.5rem',
      align: 'right',
      mono: true,
      sortable: true,
      headerClassName: SORT_HEADER,
      cell: (row) => (
        <span
          title="New cost against the current cost, at the last 4 weeks' sales, per week"
          className={cn(
            (row.weeklyImpactCents ?? 0) > 0
              ? 'text-negative'
              : (row.weeklyImpactCents ?? 0) < 0
                ? 'text-positive'
                : 'text-ink-faint'
          )}
        >
          {impactText(row.weeklyImpactCents)}
        </span>
      ),
    },
    {
      id: 'sold',
      header: 'Sold, 4 wk',
      width: '5rem',
      align: 'right',
      mono: true,
      cell: (row) => (row.units4w === undefined ? '—' : qty(row.units4w)),
    },
    {
      id: 'pack',
      header: 'Pack',
      width: '3rem',
      align: 'right',
      mono: true,
      hideBelow: 'lg',
      cell: (row) => (row.packSize ? num(row.packSize) : '—'),
    },
    {
      id: 'age',
      header: 'Age',
      width: '5rem',
      align: 'right',
      mono: true,
      sortable: true,
      headerClassName: SORT_HEADER,
      cell: (row) => (
        <span title={`Raised ${date(row.createdAt)}`}>{ageText(row.ageDays)}</span>
      ),
    },
    {
      id: 'flags',
      header: 'Check',
      width: '8.5rem',
      cell: (row) => <CheckBadges row={row} />,
    },
    {
      id: 'actions',
      header: '',
      width: '4.5rem',
      cell: (row) =>
        row.status === 'pending' ? (
          <div className="flex justify-end gap-1">
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Approve ${row.productName}`}
              onClick={(e) => {
                e.stopPropagation();
                openAction('approve', [row]);
              }}
            >
              <Check className="text-positive size-3.5" />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Reject ${row.productName}`}
              onClick={(e) => {
                e.stopPropagation();
                openAction('reject', [row]);
              }}
            >
              <X className="text-negative size-3.5" />
            </Button>
          </div>
        ) : (
          <div className="text-right">
            <Badge tone={row.status === 'approved' ? 'positive' : 'neutral'}>
              {row.status}
            </Badge>
          </div>
        ),
    },
  ];

  const busy = single.isPending || bulk.isPending;

  return (
    <Page>
      <PageHeader
        eyebrow="Buying"
        title="Purchasing"
        subtitle="Cost changes to approve: cost moves from receiving that landed above the approval threshold"
        tabs={<PurchasingTabs />}
      />

      <Toolbar>
        <ChipGroup<Status>
          value={search.status}
          onChange={(status) => {
            setSelected(new Set());
            setSearch({ status, page: 1, sortBy: undefined, sortDir: undefined });
          }}
          options={STATUSES.map((option) => ({
            value: option.value,
            label: option.label,
            ...(option.value === 'pending'
              ? { count: count.data?.count, tone: 'warn' as const }
              : {}),
          }))}
        />
        <span className="text-2xs text-ink-faint ml-auto font-mono tabular-nums">
          {num(list.data?.total ?? 0)} on this filter
        </span>
      </Toolbar>

      <div className="border-line shrink-0 border-b px-4 py-3">
        <StatRow className="md:grid-cols-4 xl:grid-cols-4">
          <Stat
            label="Awaiting review"
            icon={<ScrollText />}
            tone={(count.data?.count ?? 0) > 0 ? 'warn' : undefined}
            value={count.isPending ? '—' : num(count.data?.count ?? 0)}
            sub="across every vendor"
          />
          <Stat
            label="Weekly impact on this page"
            value={impactText(totals.impact)}
            tone={
              totals.impact > 0 ? 'negative' : totals.impact < 0 ? 'positive' : undefined
            }
            sub="new cost against current, at the last 4 weeks' sales"
          />
          <Stat
            label="Largest move"
            value={totals.worst > 0 ? `${totals.worst.toFixed(1)}%` : '—'}
            sub="on this page"
          />
          <Stat
            label="Retired by newer deliveries"
            value={
              count.data?.retiredByNewerReceipts30d === undefined
                ? '—'
                : num(count.data.retiredByNewerReceipts30d)
            }
            sub="in the last 30 days"
          />
        </StatRow>
      </div>

      <PageBody bleed className="relative flex min-h-0 flex-col">
        <DataTable
          density="comfortable"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          rowTone={(row) =>
            row.newerReceivedCost
              ? 'negative'
              : Math.abs(n(row.pctChange)) >= 25
                ? 'warn'
                : null
          }
          onRowOpen={(row) => toggleOpen(row.id)}
          renderExpanded={(row) => (open.has(row.id) ? <CostDetails row={row} /> : null)}
          sort={{
            by: sortBy,
            dir: sortDir,
            onChange: (by, dir) =>
              setSearch({
                sortBy: by === 'age' ? 'age' : 'impact',
                sortDir: dir,
                page: 1,
              }),
          }}
          {...(search.status === 'pending'
            ? { selection: { selected, onChange: setSelected } }
            : {})}
          empty={{
            icon: <ScrollText />,
            title:
              search.status === 'pending'
                ? 'Nothing awaiting approval'
                : `No ${search.status} cost changes`,
            description:
              search.status === 'pending'
                ? 'Receiving applies small cost moves automatically; only moves above the threshold land here.'
                : undefined,
          }}
          footer={
            <Pagination
              className="w-full"
              page={search.page}
              pageSize={PAGE_SIZE}
              total={list.data?.total ?? 0}
              onPageChange={(page) => setSearch({ page })}
            />
          }
        />

        <BulkBar count={selectedRows.length} onClear={() => setSelected(new Set())}>
          <Button
            size="xs"
            variant="ghost"
            loading={busy}
            onClick={() => openAction('approve', selectedRows)}
          >
            <Check className="text-positive size-3" /> Approve
          </Button>
          <Button
            size="xs"
            variant="ghost"
            loading={busy}
            onClick={() => openAction('reject', selectedRows)}
          >
            <X className="text-negative size-3" /> Reject
          </Button>
        </BulkBar>
      </PageBody>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setReviewNotes('');
          }
        }}
        title={
          pending?.action === 'approve'
            ? `Approve ${countLabel(pending.ids.length, 'cost change')}?`
            : `Reject ${countLabel(pending?.ids.length ?? 0, 'cost change')}?`
        }
        confirmLabel={pending?.action === 'approve' ? 'Approve' : 'Reject'}
        variant={pending?.action === 'approve' ? 'primary' : 'danger'}
        loading={busy}
        consequences={
          pending?.action === 'approve'
            ? [
                `Writes the new unit cost onto ${countLabel(pending.ids.length, 'product')}.`,
                'Recomputes each product’s average cost from its last 10 received PO lines.',
                'Logged to the price-change log against your user.',
                pending.ids.length > BULK_LIMIT
                  ? `Only the first ${BULK_LIMIT} can be sent in one request — the rest stay pending.`
                  : 'Cannot be undone from here; a later receipt is what changes cost again.',
              ]
            : [
                `Marks ${countLabel(pending?.ids.length ?? 0, 'change')} as rejected.`,
                'Product cost and average cost are left exactly as they are.',
              ]
        }
        onConfirm={() => {
          if (!pending) return;
          const ids = pending.ids.slice(0, BULK_LIMIT);
          const first = ids[0];
          const rowsToSend = pending.rows.filter((row) => ids.includes(row.id));
          if (ids.length === 1 && first !== undefined && rowsToSend[0]) {
            single.mutate({ row: rowsToSend[0], action: pending.action });
          } else {
            bulk.mutate({ rows: rowsToSend, action: pending.action });
          }
        }}
      >
        {pending ? (
          <div className="space-y-2">
            <div className="border-line bg-surface-sunken text-2xs max-h-40 space-y-1 overflow-y-auto rounded border p-2">
              {pending.rows.slice(0, 25).map((row) => (
                <div key={row.id} className="flex items-baseline justify-between gap-2">
                  <span className="text-ink min-w-0 truncate">{row.productName}</span>
                  <span className="text-ink-muted shrink-0 font-mono tabular-nums">
                    {money(row.oldCost)} → {money(row.newCost)}
                  </span>
                </div>
              ))}
              {pending.rows.length > 25 ? (
                <div className="text-ink-faint">…and {pending.rows.length - 25} more</div>
              ) : null}
            </div>
            <Field
              label="Review note"
              hint="Optional. Stored on every record in this batch."
            >
              <Textarea
                rows={2}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.currentTarget.value)}
              />
            </Field>
            {pending.rows.length === 1 && pending.rows[0] ? (
              <p className="text-2xs text-ink-faint">
                Raised from <Code>PO #{pending.rows[0].purchaseOrderId}</Code>
                {pending.rows[0].invoiceNumber
                  ? ` · invoice ${pending.rows[0].invoiceNumber}`
                  : ''}
              </p>
            ) : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </Page>
  );
}
